import { Button, Modal } from '@pantry/ui'
import React, { useEffect, useState } from 'react'
import {
  errorTextStyle,
  helpTextStyle,
  inputStyle,
  labelStyle,
} from '../styles'

interface ScheduleCampaignDialogProps {
  open: boolean
  title: string
  // Prefills the picker (e.g. a paused campaign's stale scheduled_at).
  initialValue?: string
  confirmLabel?: string
  isSubmitting: boolean
  onClose: () => void
  // Receives the chosen time as an ISO string (always in the future).
  onConfirm: (scheduledAtIso: string) => void
}

// datetime-local value for "now + minutes", in the user's local timezone.
const localInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Small "pick a future date & time" dialog used by draft→Schedule and by
// paused→Resume when the stored scheduled_at has already passed.
export const ScheduleCampaignDialog: React.FC<ScheduleCampaignDialogProps> = ({
  open,
  title,
  initialValue,
  confirmLabel = 'Schedule',
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = useState('')

  useEffect(() => {
    if (!open) return
    const initial = initialValue ? new Date(initialValue) : null
    const isFuture =
      initial && !Number.isNaN(initial.getTime()) && initial > new Date()
    setValue(isFuture ? localInputValue(initial) : '')
  }, [open, initialValue])

  const parsed = value ? new Date(value) : null
  const isValid =
    parsed !== null && !Number.isNaN(parsed.getTime()) && parsed > new Date()

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? () => {} : onClose}
      title={title}
      width={400}
      zIndex={1500}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!isValid || isSubmitting}
            onClick={() => {
              if (parsed) onConfirm(parsed.toISOString())
            }}
          >
            {isSubmitting ? 'Saving…' : confirmLabel}
          </Button>
        </div>
      }
    >
      <div>
        <span style={labelStyle}>Send date &amp; time</span>
        <input
          style={inputStyle}
          type="datetime-local"
          value={value}
          min={localInputValue(new Date())}
          onChange={(e) => setValue(e.target.value)}
        />
        {value && !isValid && (
          <div style={{ ...errorTextStyle, marginTop: 4 }}>
            Pick a date &amp; time in the future
          </div>
        )}
        <div style={helpTextStyle}>
          Times are in your local timezone (
          {Intl.DateTimeFormat().resolvedOptions().timeZone}).
        </div>
      </div>
    </Modal>
  )
}

export { localInputValue }
